import React from 'react';
import { motion } from 'framer-motion';
import { HomeIcon, PersonStandingIcon, CreditCardIcon, BellIcon, UserIcon, SearchIcon, MapPinIcon, CalendarIcon, ChevronDownIcon, HeartIcon } from 'lucide-react';
type Bilingual = {
  en: string;
  fr: string;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export const PHOTO_MULTISPORT = 'https://cdn.magicpatterns.com/patterns/generated-images/f681b547-5d46-4886-a2f2-a68d4818b7d7.jpg';
const PHOTO_ARTS = 'https://cdn.magicpatterns.com/patterns/generated-images/fa2d4a4b-d7fb-4c59-8c7c-5378c0740642.jpg';
const PHOTO_AQUATIC = 'https://cdn.magicpatterns.com/patterns/generated-images/63449ba5-49b8-4653-86cc-cfc1450645e0.jpg';
const NAV: {
  icon: typeof HomeIcon;
  label: Bilingual;
  active?: boolean;
  dot?: boolean;
}[] = [{
  icon: HomeIcon,
  label: {
    fr: 'Accueil',
    en: 'Home'
  }
}, {
  icon: PersonStandingIcon,
  label: {
    fr: 'Loisirs',
    en: 'Recreation'
  },
  active: true
}, {
  icon: CreditCardIcon,
  label: {
    fr: 'Carte',
    en: 'Card'
  }
}, {
  icon: BellIcon,
  label: {
    fr: 'Notifications',
    en: 'Notifications'
  },
  dot: true
}, {
  icon: UserIcon,
  label: {
    fr: 'Profil',
    en: 'Profile'
  }
}];
const FILTERS: Bilingual[] = [{
  fr: 'Toutes',
  en: 'All'
}, {
  fr: 'Camps de jour',
  en: 'Day camps'
}, {
  fr: 'Sports',
  en: 'Sports'
}, {
  fr: 'Arts',
  en: 'Arts'
}, {
  fr: 'Plein air',
  en: 'Outdoors'
}, {
  fr: 'Aquatique',
  en: 'Aquatics'
}, {
  fr: 'Culture',
  en: 'Culture'
}];
const CAMPS: {
  photo: string;
  title: Bilingual;
  place: string;
  liked?: boolean;
}[] = [{
  photo: PHOTO_MULTISPORT,
  title: {
    fr: 'Camp multisport',
    en: 'Multisport camp'
  },
  place: 'École Saint-Gabriel',
  liked: true
}, {
  photo: PHOTO_ARTS,
  title: {
    fr: 'Camp d’arts créatifs',
    en: 'Creative arts camp'
  },
  place: 'Maison de la famille'
}, {
  photo: PHOTO_AQUATIC,
  title: {
    fr: 'Camp aquatique',
    en: 'Aquatic camp'
  },
  place: 'Complexe aquatique',
  liked: true
}];
export function LoisirsDesktopMock({
  isFR
}: {
  isFR: boolean;
}) {
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  return <div className="rounded-[20px] border-[7px] border-[#1C1F2A] bg-white shadow-[0_24px_60px_rgba(18,18,40,0.14)] overflow-hidden" aria-hidden="true" data-id="element-4196" data-mp="2gekeo4d1u1ki">
      {/* App navigation */}
      <div className="flex items-center gap-4 border-b border-[#EEF0F4] px-5 h-12" data-id="element-4197" data-mp="2pgbj75vus2cs">
        <span className="font-sans text-[13px] font-bold tracking-tight text-[#121212] whitespace-nowrap" data-id="element-4198" data-mp="2udtvn692wzpj">
          BCITI <span className="font-light text-[#121212]/30" data-id="element-4199" data-mp="1wsph6229x5s1">|</span> ATNA
        </span>
        <nav className="hidden sm:flex items-center gap-1 mx-auto" data-id="element-4200" data-mp="21r93i3ks4jkx">
          {NAV.map(({
          icon: Icon,
          label,
          active,
          dot
        }) => <span key={label.en} className={`relative flex items-center gap-1.5 px-2.5 h-12 font-sans text-[10.5px] ${active ? 'text-[#4F46E5] bg-[#F3F2FE]' : 'text-[#5B6478]'}`} data-id="element-4201" data-mp="26nzfoftgqv12">
              <Icon className="w-3.5 h-3.5" strokeWidth={1.8} data-id="element-4202" data-mp="1iyy44jpc33rk" />
              {pick(label)}
              {dot && <span className="absolute top-3 left-4 w-1.5 h-1.5 rounded-full bg-[#EF4444]" data-id="element-4203" data-mp="1bggumdhgjoyl" />}
              {active && <span className="absolute left-2 right-2 bottom-0 h-0.5 bg-[#4F46E5]" data-id="element-4204" data-mp="16i5i1uzs30co" />}
            </span>)}
        </nav>
        <span className="ml-auto sm:ml-0 flex items-center gap-3" data-id="element-4205" data-mp="11g6f2m7ya5g9">
          <SearchIcon className="w-3.5 h-3.5 text-[#5B6478]" data-id="element-4206" data-mp="whwiq8ahoyt2" />
          <span className="font-sans text-[10.5px] font-semibold text-[#121212]" data-id="element-4207" data-mp="shcmd6xqpz7z">{isFR ? 'FR' : 'EN'}</span>
        </span>
      </div>

      {/* Content */}
      <div className="bg-[#FBFBFD] px-5 pt-5 pb-6" data-id="element-4208" data-mp="np1n86atqhgh">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-4209" data-mp="ir9td6qmcl9n">
          <div data-id="element-4210" data-mp="ip6tluznzdrq">
            <p className="font-sans text-[17px] font-semibold text-[#121212]" data-id="element-4211" data-mp="dsot57bda1l7">
              {isFR ? 'Activités et camps' : 'Activities and camps'}
            </p>
            <p className="font-sans text-[11px] text-[#5B6478] mt-0.5" data-id="element-4212" data-mp="8sk9j8f5wqqr">
              {isFR ? 'Découvrez des activités pour toute la famille' : 'Discover activities for the whole family'}
            </p>
          </div>
          <div className="flex gap-2" data-id="element-4213" data-mp="tkqycboi2d0y">
            <span className="flex items-center gap-1.5 rounded-lg border border-[#E4E6EC] bg-white px-2.5 py-1.5 font-sans text-[10.5px] text-[#121212]" data-id="element-4214" data-mp="yhr5yr0zznu6">
              <MapPinIcon className="w-3 h-3 text-[#5B6478]" data-id="element-4215" data-mp="ko9u7sejzfep" /> Brossard <ChevronDownIcon className="w-3 h-3 text-[#9AA0AE]" data-id="element-4216" data-mp="oqd8spwir047" />
            </span>
            <span className="flex items-center gap-1.5 rounded-lg border border-[#E4E6EC] bg-white px-2.5 py-1.5 font-sans text-[10.5px] font-medium text-[#121212]" data-id="element-4217" data-mp="aqvfbx8a2yoi">
              <CalendarIcon className="w-3 h-3 text-[#5B6478]" data-id="element-4218" data-mp="1r8fhfv760xk" /> {isFR ? 'Été 2026' : 'Summer 2026'}
              <ChevronDownIcon className="w-3 h-3 text-[#9AA0AE]" data-id="element-4219" data-mp="5sk5hmhzgo6a" />
            </span>
          </div>
        </div>

        <div className="mt-3.5 flex items-center gap-2 rounded-xl border border-[#E4E6EC] bg-white px-3 py-2.5" data-id="element-4220" data-mp="1w4q5ramzlbwa">
          <SearchIcon className="w-3.5 h-3.5 text-[#9AA0AE]" data-id="element-4221" data-mp="205tiho9qmrta" />
          <span className="font-sans text-[11px] text-[#9AA0AE]" data-id="element-4222" data-mp="3kovmpxr7ex70">
            {isFR ? 'Rechercher une activité, un camp ou un lieu…' : 'Search for an activity, camp or place…'}
          </span>
        </div>

        <div className="mt-3 flex gap-1.5 overflow-hidden" data-id="element-4223" data-mp="3uef6r6qnkx3g">
          {FILTERS.map((f, i) => <span key={f.en} className={`shrink-0 rounded-full border px-2.5 py-1 font-sans text-[10px] ${i === 1 ? 'border-[#C7C4F7] bg-[#EEEDFD] text-[#4F46E5] font-medium' : 'border-[#E4E6EC] bg-white text-[#5B6478]'}`} data-id="element-4224" data-mp="3po8k3cfpbmnu" data-mpi={i}>
              {pick(f)}
            </span>)}
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-4225" data-mp="h62nkpz6aawd">
          {CAMPS.map((camp, i) => <motion.div key={camp.title.en} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.35 + i * 0.06,
          ease: EASE
        }} whileHover={{
          y: -3
        }} className={`rounded-xl border border-[#ECEEF2] bg-white overflow-hidden transition-shadow duration-200 hover:shadow-[0_10px_24px_rgba(18,18,40,0.08)] ${i === 2 ? 'hidden sm:block' : ''}`} data-id="element-4226" data-mp="c6zkhb2r6er1" data-mpi={i}>
              <div className="relative" data-id="element-4227" data-mp="vx3ht8pl6vtz">
                <img src={camp.photo} alt="" className="w-full aspect-[4/3] object-cover" data-id="element-4228" data-mp="vy4vlkxzlwfe" />
                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white flex items-center justify-center" data-id="element-4229" data-mp="10v503rf6ur4e">
                  <HeartIcon className={`w-3 h-3 ${camp.liked ? 'text-[#EF4444] fill-[#EF4444]' : 'text-[#9AA0AE]'}`} data-id="element-4230" data-mp="15tonzxdcecze" />
                </span>
              </div>
              <div className="p-2.5" data-id="element-4231" data-mp="1fkjb13yexglw">
                <p className="font-sans text-[11px] font-semibold text-[#121212] truncate" data-id="element-4232" data-mp="1jmo33y97q56f">{pick(camp.title)}</p>
                <p className="font-sans text-[9.5px] text-[#5B6478] mt-0.5" data-id="element-4233" data-mp="1olhdhht7dg2d">{isFR ? '5 à 12 ans' : 'Ages 5 to 12'}</p>
                <p className="flex items-center gap-1 font-sans text-[9.5px] text-[#5B6478] mt-1.5" data-id="element-4234" data-mp="3941n0z3nnfaa">
                  <CalendarIcon className="w-2.5 h-2.5 shrink-0" data-id="element-4235" data-mp="343ngjlhpcded" />
                  <span className="truncate" data-id="element-4236" data-mp="3033ga91u0to0">{isFR ? 'Du 6 juillet au 14 août 2026' : 'July 6 to Aug 14, 2026'}</span>
                </p>
                <p className="flex items-center gap-1 font-sans text-[9.5px] text-[#5B6478] mt-0.5" data-id="element-4237" data-mp="2v5213z7j1eht">
                  <MapPinIcon className="w-2.5 h-2.5 shrink-0" data-id="element-4238" data-mp="3rszmmchqg5cg" />
                  <span className="truncate" data-id="element-4239" data-mp="3nsxxt3x3j6el">{camp.place}</span>
                </p>
                <span className="mt-2 block rounded-md bg-[#F3F2FE] py-1 text-center font-sans text-[9.5px] font-medium text-[#4F46E5]" data-id="element-4240" data-mp="3j1zd7s52rm5j">
                  {isFR ? 'Voir les détails' : 'View details'}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}